import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { 
  ListRenderingReleaseItemsRequest, 
  ListRenderingReleaseItemsResponse,
  ReleaseItem,
  SituViewWebApiClient,
  ProductName,
  ReleaseStatus
} from 'situview-webapi-client';
import { AuthService, ProductNameService, StateService, ReleaseService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { trigger, style, transition, animate, state } from '@angular/animations';

@Component({
  selector: 'app-release-overview',
  templateUrl: './release-overview.component.html',
  styleUrls: ['./release-overview.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0px', overflow: 'hidden' })),
      state('expanded', style({ height: '*' })),
      transition('collapsed <=> expanded', animate('200ms ease-in-out'))
    ]),
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class ReleaseOverviewComponent extends SituBaseComponent implements OnInit, OnChanges {
  @Input() companyId: string | undefined;
  @Input() releaseId: string | undefined;
  @Input() isDarkModeInput: boolean | undefined;
  loading: boolean = false;
  expanded: boolean = false; // Collapsed by default
  selectedSection: 'inflight' | 'daydream' | 'upcoming' | 'completed' | null = null;
  inFlightItems: ReleaseItem[] = [];
  /** Facade items whose Daydream is being generated: not a renderer's work, so listed apart from the renders. */
  daydreamItems: ReleaseItem[] = [];
  upcomingItems: ReleaseItem[] = [];
  recentlyCompletedItems: ReleaseItem[] = [];
  
  productNames = ProductName;
  releaseStatuses = ReleaseStatus;
  
  // Configuration
  upcomingLimit: number = 10;
  recentlyCompletedLimit: number = 10;
  inFlightLimit: number = 10;
  recentlyCompletedHours: number = 24;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situViewApiClient: SituViewWebApiClient,
    private snackBar: MatSnackBar,
    private router: Router,
    private productNameService: ProductNameService,
    private releaseService: ReleaseService
  ) { 
    super(authService, stateService);
  }

  ngOnInit(): void {
    // Load data when component initializes if company ID or release ID is already set
    if (this.companyId || this.releaseId) {
      this.loadReleaseItems();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['companyId'] || changes['releaseId']) && (this.companyId || this.releaseId)) {
      this.loadReleaseItems();
    }
    
    // Update isDarkMode from input if provided
    if (changes['isDarkModeInput'] && this.isDarkModeInput !== undefined) {
      this.isDarkMode = this.isDarkModeInput;
    }
  }

  protected onSelectedCompanyChange(): void {
    // This is now handled by ngOnChanges
  }

  protected onThemeChange(): void {
    // Refresh the view when theme changes to update status colors
    // No specific action needed as getStatusColor() uses this.isDarkMode dynamically
  }

  loadReleaseItems(): void {
    if (!this.companyId && !this.releaseId) {
      return;
    }
    
    this.loading = true;
    
    const request = new ListRenderingReleaseItemsRequest();
    if (this.companyId) {
      request.companyId = this.companyId;
    }
    if (this.releaseId) {
      request.releaseId = this.releaseId;
    }
    request.upcomingLimit = this.upcomingLimit;
    request.recentlyCompletedLimit = this.recentlyCompletedLimit;
    request.recentlyCompletedHours = this.recentlyCompletedHours;
    // Add inFlightLimit if the API supports it
    if ('inFlightLimit' in request) {
      (request as any).inFlightLimit = this.inFlightLimit;
    }
    
    
    this.situViewApiClient.listRenderingReleaseItems(request)
      .then((response: ListRenderingReleaseItemsResponse) => {
        if (response.success) {
          this.inFlightItems = response.inFlightItems || [];
          this.daydreamItems = response.daydreamItems || [];
          this.upcomingItems = response.upcomingItems || [];
          this.recentlyCompletedItems = response.recentlyCompletedItems || [];
          
          // Auto-select section if there's data
          if (!this.selectedSection) {
            if (this.inFlightItems.length > 0) {
              this.selectedSection = 'inflight';
            } else if (this.daydreamItems.length > 0) {
              this.selectedSection = 'daydream';
            } else if (this.upcomingItems.length > 0) {
              this.selectedSection = 'upcoming';
            } else if (this.recentlyCompletedItems.length > 0) {
              this.selectedSection = 'completed';
            }
          }
        } else {
          this.showMessage('Failed to load release items');
        }
      })
      .catch(error => {
        this.showMessage('Error loading release items');
      })
      .finally(() => {
        this.loading = false;
      });
  }

  refreshData(): void {
    this.loadReleaseItems();
  }

  navigateToRelease(releaseId: string | undefined): void {
    if (releaseId) {
      this.router.navigate(['/admin/release', releaseId]);
    }
  }

  navigateToReleaseItem(releaseItem: any): void {
    if (releaseItem.releaseId) {
      // Navigate to the release page with the specific release item highlighted
      this.router.navigate(['/admin/release', releaseItem.releaseId], {
        queryParams: {
          releaseItemId: releaseItem.id,
          homeConfigId: releaseItem.homeConfigId,
          home: releaseItem.home,
          masterFile: releaseItem.masterFile,
          options: releaseItem.options,
          styleTemplate: releaseItem.styleTemplate,
          productName: releaseItem.productName
        }
      });
    }
  }

  openLinkInNewWindow(event: Event, route: string, params: any): void {
    event.stopPropagation();
    const url = this.router.createUrlTree([route], { queryParams: params }).toString();
    window.open(url, '_blank');
  }

  getProductColour(productName: ProductName): string {
    return this.productNameService.getProductNameConfiguration(productName as any)?.productColor || '';
  }

  getStatusClass(status: ReleaseStatus): string {
    switch (status) {
      case ReleaseStatus.Live:
        return 'text-success';
      case ReleaseStatus.StandardReviewed:
        return 'text-info';
      case ReleaseStatus.ReadyForLive:
        return 'text-info';
      case ReleaseStatus.ReadyForRender:
        return 'text-warning';
      case ReleaseStatus.RequiresAttentionSitu:
        return 'text-danger';
      default:
        return '';
    }
  }

  getStatusText(status: ReleaseStatus): string {
    return this.releaseStatuses[status] || 'Unknown';
  }

  getStatusColor(status: ReleaseStatus): string {
    // Use input isDarkMode if provided, otherwise fall back to base component's isDarkMode
    const darkMode = this.isDarkModeInput !== undefined ? this.isDarkModeInput : this.isDarkMode;
    return this.releaseService.getStatusColor(status, darkMode);
  }

  // Optimized method to avoid repeated calculations in templates
  getStatusInfo(status: ReleaseStatus): { text: string; color: string; class: string } {
    const darkMode = this.isDarkModeInput !== undefined ? this.isDarkModeInput : this.isDarkMode;
    return {
      text: this.getStatusText(status),
      color: this.releaseService.getStatusColor(status, darkMode),
      class: this.getStatusClass(status)
    };
  }

  trackByItemId(index: number, item: ReleaseItem): string | undefined {
    return item.id;
  }

  selectSection(section: 'inflight' | 'daydream' | 'upcoming' | 'completed'): void {
    this.selectedSection = section;
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  getProductIcon(productName: ProductName): string {
    switch (productName) {
      case this.productNames.Plan:
        return 'foundation';
      case this.productNames.Render:
        return 'photo_camera';
      case this.productNames.RayTracedPlus:
      case this.productNames.RayTracedDraft:
      case this.productNames.RayTraced:
      case this.productNames.Panorama:
        return 'panorama';
      case this.productNames.Facade:
        return 'image';
      case this.productNames.VR:
        return 'view_in_ar';
      case this.productNames.Vanity:
        return 'design_services';
      default:
        return 'image';
    }
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }
}